import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Sun, ArrowLeft, Plus, Trash2, Edit, Wifi, WifiOff, RefreshCw, 
  Cloud, Router, Settings, CheckCircle, XCircle, Zap, Save, Globe
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ConfiguracaoFotovoltaico = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cloudConfigured, setCloudConfigured] = useState(false);
  const [cloudConfig, setCloudConfig] = useState(null); // Dados da cloud configurada
  const [cloudDevices, setCloudDevices] = useState([]);
  const [loadingCloud, setLoadingCloud] = useState(false);
  const [refreshingDevices, setRefreshingDevices] = useState(false);
  
  // Cloud config form
  const [cloudForm, setCloudForm] = useState({
    server_url: '',
    auth_key: ''
  });
  
  // Device form
  const [deviceForm, setDeviceForm] = useState({
    device_id: '',
    local: '',
    descricao: '',
    connection_type: 'cloud',
    ip_address: '',
    shelly_generation: 'gen1',
    installed_power_kw: 0
  });
  const [editingDevice, setEditingDevice] = useState(null);
  const [showDeviceDialog, setShowDeviceDialog] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);

  // ===== Huawei FusionSolar =====
  const [huaweiStations, setHuaweiStations] = useState([]);
  const [showHuaweiDialog, setShowHuaweiDialog] = useState(false);
  const [editingHuawei, setEditingHuawei] = useState(null);
  const [huaweiForm, setHuaweiForm] = useState({
    kiosk_key: '',
    local: '',
    descricao: '',
    installed_power_kw: 0
  });
  const [testingHuawei, setTestingHuawei] = useState(false);
  const [huaweiTestResult, setHuaweiTestResult] = useState(null);

  const loadHuaweiStations = async () => {
    try {
      const r = await fetch(`${API_URL}/api/huawei/stations`);
      const d = await r.json();
      setHuaweiStations(Array.isArray(d) ? d : []);
    } catch (e) {
      console.error('Erro ao carregar estações Huawei:', e);
    }
  };

  const resetHuaweiForm = () => {
    setHuaweiForm({ kiosk_key: '', local: '', descricao: '', installed_power_kw: 0 });
    setEditingHuawei(null);
    setHuaweiTestResult(null);
  };

  const handleTestHuaweiKey = async () => {
    if (!huaweiForm.kiosk_key) { toast.error('Insira a Kiosk Key'); return; }
    setTestingHuawei(true);
    setHuaweiTestResult(null);
    try {
      const r = await fetch(`${API_URL}/api/huawei/test-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kiosk_key: huaweiForm.kiosk_key })
      });
      const d = await r.json();
      setHuaweiTestResult(d);
      if (d.success) {
        toast.success(`Estação encontrada: ${d.station_overview?.stationName || ''}`);
        if (!huaweiForm.local && d.station_overview?.stationName) {
          setHuaweiForm(prev => ({ ...prev, local: d.station_overview.stationName }));
        }
      } else {
        toast.error(d.error || 'Kiosk Key inválida');
      }
    } catch (e) {
      toast.error('Erro de conexão');
    }
    setTestingHuawei(false);
  };

  const handleSaveHuawei = async () => {
    if (!huaweiForm.kiosk_key || !huaweiForm.local) {
      toast.error('Preencha Kiosk Key e Local');
      return;
    }
    try {
      const url = editingHuawei
        ? `${API_URL}/api/huawei/stations/${editingHuawei.id}`
        : `${API_URL}/api/huawei/stations`;
      const method = editingHuawei ? 'PUT' : 'POST';
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(huaweiForm)
      });
      const d = await r.json();
      if (r.ok && (d.success || d.station)) {
        toast.success(editingHuawei ? 'Estação atualizada!' : 'Estação adicionada!');
        setShowHuaweiDialog(false);
        resetHuaweiForm();
        loadHuaweiStations();
      } else {
        toast.error(d.detail || 'Erro ao guardar');
      }
    } catch (e) {
      toast.error('Erro de conexão');
    }
  };

  const handleEditHuawei = (station) => {
    setEditingHuawei(station);
    setHuaweiForm({
      kiosk_key: station.kiosk_key,
      local: station.local,
      descricao: station.descricao || '',
      installed_power_kw: station.installed_power_kw || 0
    });
    setHuaweiTestResult(null);
    setShowHuaweiDialog(true);
  };

  const handleDeleteHuawei = async (id) => {
    if (!window.confirm('Remover esta estação Huawei?')) return;
    try {
      const r = await fetch(`${API_URL}/api/huawei/stations/${id}`, { method: 'DELETE' });
      const d = await r.json();
      if (d.success) {
        toast.success('Estação removida');
        loadHuaweiStations();
      } else {
        toast.error(d.detail || 'Erro ao remover');
      }
    } catch (e) {
      toast.error('Erro de conexão');
    }
  };

  const handleRefreshHuawei = async () => {
    toast.info('A atualizar estações Huawei...');
    try {
      const r = await fetch(`${API_URL}/api/huawei/refresh-all`, { method: 'POST' });
      const d = await r.json();
      if (d.success) {
        toast.success(`Snapshots: ${d.refreshed}/${d.total}`);
        loadHuaweiStations();
      }
    } catch (e) {
      toast.error('Erro ao atualizar');
    }
  };

  // Carregar dispositivos
  const loadDevices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/shelly/devices`);
      const data = await response.json();
      setDevices(data || []);
    } catch (error) {
      console.error('Erro ao carregar dispositivos:', error);
    }
  };

  // Verificar configuração da cloud
  const checkCloudConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/shelly/cloud-configured`);
      const data = await response.json();
      setCloudConfigured(data.configured);
      setCloudConfig(data);
      if (data.server_url) {
        setCloudForm(prev => ({ ...prev, server_url: data.server_url }));
      }
    } catch (error) {
      console.error('Erro ao verificar cloud:', error);
    }
  };

  // Atualizar leituras de todos os dispositivos
  const refreshAllDevices = async () => {
    setRefreshingDevices(true);
    try {
      const response = await fetch(`${API_URL}/api/shelly/refresh-all`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Leituras atualizadas: ${data.refreshed}/${data.total} dispositivos`);
        // Recarregar dispositivos para mostrar últimas leituras
        loadDevices();
      } else {
        toast.error('Erro ao atualizar leituras');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
    setRefreshingDevices(false);
  };

  // Obter status de um dispositivo específico
  const getDeviceStatus = async (deviceId) => {
    try {
      const response = await fetch(`${API_URL}/api/shelly/status/${deviceId}`);
      const data = await response.json();
      
      if (data.success) {
        toast.success('Leitura obtida com sucesso!');
        return data.data;
      } else {
        toast.error(data.error || 'Erro ao obter leitura');
        return null;
      }
    } catch (error) {
      toast.error('Erro de conexão');
      return null;
    }
  };

  // Carregar dispositivos da cloud
  const loadCloudDevices = async () => {
    setLoadingCloud(true);
    try {
      const response = await fetch(`${API_URL}/api/shelly/cloud-devices`);
      const data = await response.json();
      if (data.success) {
        setCloudDevices(data.devices || []);
      } else {
        toast.error(data.error || 'Erro ao carregar dispositivos da cloud');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
    setLoadingCloud(false);
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadDevices(), checkCloudConfig(), loadHuaweiStations()]);
      setLoading(false);
    };
    init();
  }, []);

  // Configurar Shelly Cloud
  const handleConfigureCloud = async () => {
    if (!cloudForm.auth_key) {
      toast.error('Insira a Auth Key da Shelly Cloud');
      return;
    }

    setTestingConnection(true);
    try {
      const response = await fetch(`${API_URL}/api/shelly/configure-cloud`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cloudForm)
      });
      const data = await response.json();

      if (data.success) {
        toast.success('Shelly Cloud configurada com sucesso!');
        setCloudConfigured(true);
        loadCloudDevices();
      } else {
        toast.error(data.error || 'Erro ao configurar cloud');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
    setTestingConnection(false);
  };

  // Testar conexão local
  const handleTestLocal = async () => {
    if (!deviceForm.ip_address) {
      toast.error('Insira o endereço IP do dispositivo');
      return;
    }

    setTestingConnection(true);
    try {
      const response = await fetch(`${API_URL}/api/shelly/test-local?ip_address=${deviceForm.ip_address}`, {
        method: 'POST'
      });
      const data = await response.json();

      if (data.success) {
        toast.success(`Dispositivo encontrado: ${data.device_type} (${data.generation})`);
        setDeviceForm(prev => ({ ...prev, shelly_generation: data.generation }));
      } else {
        toast.error(data.error || 'Dispositivo não encontrado');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
    setTestingConnection(false);
  };

  // Guardar dispositivo
  const handleSaveDevice = async () => {
    if (!deviceForm.device_id || !deviceForm.local) {
      toast.error('Preencha o ID do dispositivo e o local');
      return;
    }

    try {
      const url = editingDevice 
        ? `${API_URL}/api/shelly/devices/${editingDevice.device_id}`
        : `${API_URL}/api/shelly/devices`;
      
      const method = editingDevice ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(deviceForm)
      });
      const data = await response.json();

      if (data.success) {
        toast.success(editingDevice ? 'Dispositivo atualizado!' : 'Dispositivo adicionado!');
        setShowDeviceDialog(false);
        resetDeviceForm();
        loadDevices();
      } else {
        toast.error(data.detail || 'Erro ao guardar');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  // Remover dispositivo
  const handleDeleteDevice = async (deviceId) => {
    if (!window.confirm('Tem a certeza que deseja remover este dispositivo?')) return;

    try {
      const response = await fetch(`${API_URL}/api/shelly/devices/${deviceId}`, {
        method: 'DELETE'
      });
      const data = await response.json();

      if (data.success) {
        toast.success('Dispositivo removido!');
        loadDevices();
      } else {
        toast.error(data.detail || 'Erro ao remover');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  // Editar dispositivo
  const handleEditDevice = (device) => {
    setEditingDevice(device);
    setDeviceForm({
      device_id: device.device_id,
      local: device.local,
      descricao: device.descricao || '',
      connection_type: device.connection_type || 'cloud',
      ip_address: device.ip_address || '',
      shelly_generation: device.shelly_generation || 'gen1',
      installed_power_kw: device.installed_power_kw || 0
    });
    setShowDeviceDialog(true);
  };

  // Adicionar dispositivo da cloud
  const handleAddCloudDevice = (cloudDevice) => {
    setDeviceForm({
      device_id: cloudDevice.id,
      local: '',
      descricao: cloudDevice.name,
      connection_type: 'cloud',
      ip_address: '',
      shelly_generation: 'gen1',
      installed_power_kw: 0
    });
    setEditingDevice(null);
    setShowDeviceDialog(true);
  };

  const resetDeviceForm = () => {
    setDeviceForm({
      device_id: '',
      local: '',
      descricao: '',
      connection_type: 'cloud',
      ip_address: '',
      shelly_generation: 'gen1',
      installed_power_kw: 0
    });
    setEditingDevice(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/fotovoltaico')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Sun className="h-6 w-6 text-orange-500" />
              Configuração Fotovoltaico
            </h1>
            <p className="text-gray-500">Gerir dispositivos Shelly EM para monitorização de produção</p>
          </div>
        </div>
        <Badge className={cloudConfigured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
          {cloudConfigured ? (
            <><Cloud className="h-3 w-3 mr-1" /> Cloud Ativa</>
          ) : (
            <><WifiOff className="h-3 w-3 mr-1" /> Cloud não configurada</>
          )}
        </Badge>
      </div>

      <Tabs defaultValue="devices" className="space-y-4">
        <TabsList>
          <TabsTrigger value="devices" className="flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Dispositivos ({devices.length})
          </TabsTrigger>
          <TabsTrigger value="cloud" className="flex items-center gap-2">
            <Cloud className="h-4 w-4" />
            Shelly Cloud
          </TabsTrigger>
          <TabsTrigger value="huawei" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Huawei FusionSolar ({huaweiStations.length})
          </TabsTrigger>
        </TabsList>

        {/* Tab: Dispositivos */}
        <TabsContent value="devices" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Dispositivos Shelly EM</CardTitle>
                <CardDescription>Lista de dispositivos configurados para monitorização fotovoltaica</CardDescription>
              </div>
              <div className="flex gap-2">
                {devices.length > 0 && (
                  <Button 
                    variant="outline" 
                    onClick={refreshAllDevices}
                    disabled={refreshingDevices}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${refreshingDevices ? 'animate-spin' : ''}`} />
                    Obter Leituras
                  </Button>
                )}
                <Dialog open={showDeviceDialog} onOpenChange={setShowDeviceDialog}>
                  <DialogTrigger asChild>
                    <Button onClick={() => { resetDeviceForm(); setShowDeviceDialog(true); }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar Dispositivo
                    </Button>
                  </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>
                      {editingDevice ? 'Editar Dispositivo' : 'Novo Dispositivo Shelly EM'}
                    </DialogTitle>
                    <DialogDescription>
                      Configure os detalhes do dispositivo Shelly EM
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Tipo de Conexão</Label>
                        <Select 
                          value={deviceForm.connection_type} 
                          onValueChange={(v) => setDeviceForm(prev => ({ ...prev, connection_type: v }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cloud">
                              <div className="flex items-center gap-2">
                                <Cloud className="h-4 w-4" /> Shelly Cloud
                              </div>
                            </SelectItem>
                            <SelectItem value="local">
                              <div className="flex items-center gap-2">
                                <Router className="h-4 w-4" /> IP Local
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Geração Shelly</Label>
                        <Select 
                          value={deviceForm.shelly_generation} 
                          onValueChange={(v) => setDeviceForm(prev => ({ ...prev, shelly_generation: v }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="gen1">Gen 1 (Shelly EM)</SelectItem>
                            <SelectItem value="gen2">Gen 2 (Shelly Pro)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {deviceForm.connection_type === 'local' ? (
                      <div className="space-y-2">
                        <Label>Endereço IP</Label>
                        <div className="flex gap-2">
                          <Input 
                            placeholder="192.168.1.100"
                            value={deviceForm.ip_address}
                            onChange={(e) => setDeviceForm(prev => ({ ...prev, ip_address: e.target.value }))}
                          />
                          <Button 
                            variant="outline" 
                            onClick={handleTestLocal}
                            disabled={testingConnection}
                          >
                            {testingConnection ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              'Testar'
                            )}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Label>ID do Dispositivo (Shelly Cloud)</Label>
                        <Input 
                          placeholder="abc123..."
                          value={deviceForm.device_id}
                          onChange={(e) => setDeviceForm(prev => ({ ...prev, device_id: e.target.value }))}
                          disabled={!!editingDevice}
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Local *</Label>
                        <Input 
                          placeholder="Ex: Edifício A"
                          value={deviceForm.local}
                          onChange={(e) => setDeviceForm(prev => ({ ...prev, local: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Potência Instalada (kW)</Label>
                        <Input 
                          type="number"
                          step="0.1"
                          placeholder="Ex: 5.5"
                          value={deviceForm.installed_power_kw}
                          onChange={(e) => setDeviceForm(prev => ({ ...prev, installed_power_kw: parseFloat(e.target.value) || 0 }))}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Descrição</Label>
                      <Input 
                        placeholder="Ex: Painel Solar Telhado Norte"
                        value={deviceForm.descricao}
                        onChange={(e) => setDeviceForm(prev => ({ ...prev, descricao: e.target.value }))}
                      />
                    </div>
                  </div>

                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowDeviceDialog(false)}>
                      Cancelar
                    </Button>
                    <Button onClick={handleSaveDevice}>
                      <Save className="h-4 w-4 mr-2" />
                      {editingDevice ? 'Atualizar' : 'Adicionar'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {devices.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Sun className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p className="font-medium">Nenhum dispositivo configurado</p>
                  <p className="text-sm">Adicione dispositivos Shelly EM para começar a monitorizar</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Local</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Conexão</TableHead>
                      <TableHead>Potência (kW)</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {devices.map((device) => (
                      <TableRow key={device.device_id}>
                        <TableCell className="font-medium">{device.local}</TableCell>
                        <TableCell>{device.descricao || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="gap-1">
                            {device.connection_type === 'cloud' ? (
                              <><Cloud className="h-3 w-3" /> Cloud</>
                            ) : (
                              <><Router className="h-3 w-3" /> {device.ip_address}</>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell>{device.installed_power_kw || 0} kW</TableCell>
                        <TableCell>
                          {device.ativo ? (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="h-3 w-3 mr-1" /> Ativo
                            </Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-600">
                              <XCircle className="h-3 w-3 mr-1" /> Inativo
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleEditDevice(device)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => handleDeleteDevice(device.device_id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Shelly Cloud */}
        <TabsContent value="cloud" className="space-y-4">
          {/* Mostrar configuração atual se existir */}
          {cloudConfigured && cloudConfig && (
            <Card className="bg-green-50 border-green-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-800">
                  <CheckCircle className="h-5 w-5" />
                  Shelly Cloud Configurada
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Server URL</p>
                    <p className="font-mono text-sm">{cloudConfig.server_url}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Auth Key</p>
                    <p className="font-mono text-sm">{cloudConfig.auth_key_masked}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Última atualização</p>
                    <p className="text-sm">
                      {cloudConfig.updated_at 
                        ? new Date(cloudConfig.updated_at).toLocaleString('pt-PT')
                        : '-'
                      }
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cloud className="h-5 w-5" />
                {cloudConfigured ? 'Atualizar Configuração' : 'Configuração Shelly Cloud'}
              </CardTitle>
              <CardDescription>
                Configure as credenciais para aceder à Shelly Cloud API
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 max-w-xl">
                <div className="space-y-2">
                  <Label>Server URL</Label>
                  <div className="flex gap-2">
                    <Input 
                      placeholder="https://shelly-XX-eu.shelly.cloud"
                      value={cloudForm.server_url}
                      onChange={(e) => setCloudForm(prev => ({ ...prev, server_url: e.target.value }))}
                      className="flex-1"
                    />
                    <Select 
                      value=""
                      onValueChange={(v) => setCloudForm(prev => ({ ...prev, server_url: v }))}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Exemplos..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="https://shelly-55-eu.shelly.cloud">Europa (shelly-55-eu)</SelectItem>
                        <SelectItem value="https://shelly-56-eu.shelly.cloud">Europa 2 (shelly-56-eu)</SelectItem>
                        <SelectItem value="https://shelly-57-eu.shelly.cloud">Europa 3 (shelly-57-eu)</SelectItem>
                        <SelectItem value="https://shelly-58-eu.shelly.cloud">Europa 4 (shelly-58-eu)</SelectItem>
                        <SelectItem value="https://shelly-59-eu.shelly.cloud">Europa 5 (shelly-59-eu)</SelectItem>
                        <SelectItem value="https://shelly-60-eu.shelly.cloud">Europa 6 (shelly-60-eu)</SelectItem>
                        <SelectItem value="https://shelly-100-us.shelly.cloud">EUA (shelly-100-us)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-gray-500">
                    Insira o URL do servidor ou selecione um exemplo. O servidor pode ser encontrado em: Shelly App → User Settings → Authorization cloud key
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Auth Key</Label>
                  <Input 
                    type="password"
                    placeholder="Insira a sua Auth Key"
                    value={cloudForm.auth_key}
                    onChange={(e) => setCloudForm(prev => ({ ...prev, auth_key: e.target.value }))}
                  />
                  <p className="text-xs text-gray-500">
                    Encontre em: Shelly App → User Settings → Authorization cloud key → Get key
                  </p>
                </div>

                <Button 
                  onClick={handleConfigureCloud}
                  disabled={testingConnection}
                  className="w-fit"
                >
                  {testingConnection ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Settings className="h-4 w-4 mr-2" />
                  )}
                  {cloudConfigured ? 'Atualizar e Testar' : 'Configurar e Testar'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {cloudConfigured && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Dispositivos na Shelly Cloud</CardTitle>
                  <CardDescription>Dispositivos encontrados na sua conta Shelly</CardDescription>
                </div>
                <Button variant="outline" onClick={loadCloudDevices} disabled={loadingCloud}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loadingCloud ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </CardHeader>
              <CardContent>
                {loadingCloud ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin text-orange-500" />
                  </div>
                ) : cloudDevices.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Cloud className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>Nenhum dispositivo encontrado na cloud</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>ID</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="text-right">Ação</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cloudDevices.map((device) => {
                        const isAdded = devices.some(d => d.device_id === device.id);
                        return (
                          <TableRow key={device.id}>
                            <TableCell className="font-medium">{device.name}</TableCell>
                            <TableCell>{device.type}</TableCell>
                            <TableCell className="font-mono text-xs">{device.id}</TableCell>
                            <TableCell>
                              {device.online ? (
                                <Badge className="bg-green-100 text-green-700">
                                  <Wifi className="h-3 w-3 mr-1" /> Online
                                </Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-600">
                                  <WifiOff className="h-3 w-3 mr-1" /> Offline
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {isAdded ? (
                                <Badge variant="outline" className="text-green-600">
                                  <CheckCircle className="h-3 w-3 mr-1" /> Adicionado
                                </Badge>
                              ) : (
                                <Button 
                                  size="sm" 
                                  onClick={() => handleAddCloudDevice(device)}
                                >
                                  <Plus className="h-3 w-3 mr-1" /> Adicionar
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tab: Huawei FusionSolar */}
        <TabsContent value="huawei" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5 text-orange-500" />
                  Estações Huawei FusionSolar (Modo Kiosk)
                </CardTitle>
                <CardDescription>
                  Adicione estações fotovoltaicas Huawei usando a Kiosk Key partilhada
                </CardDescription>
              </div>
              <div className="flex gap-2">
                {huaweiStations.length > 0 && (
                  <Button variant="outline" onClick={handleRefreshHuawei} data-testid="huawei-refresh-btn">
                    <RefreshCw className="h-4 w-4 mr-2" /> Atualizar Snapshots
                  </Button>
                )}
                <Dialog open={showHuaweiDialog} onOpenChange={(o) => { setShowHuaweiDialog(o); if (!o) resetHuaweiForm(); }}>
                  <DialogTrigger asChild>
                    <Button onClick={() => { resetHuaweiForm(); setShowHuaweiDialog(true); }} data-testid="huawei-add-btn">
                      <Plus className="h-4 w-4 mr-2" /> Adicionar Estação
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>{editingHuawei ? 'Editar Estação Huawei' : 'Nova Estação Huawei FusionSolar'}</DialogTitle>
                      <DialogDescription>
                        Cole a Kiosk Key partilhada (parâmetro <code>kk</code> do URL Huawei).
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                      <div className="space-y-2">
                        <Label>Kiosk Key *</Label>
                        <div className="flex gap-2">
                          <Input
                            placeholder="Q5jubn7EQy4tMgur9Ld8ciywvuDB5iyg"
                            value={huaweiForm.kiosk_key}
                            onChange={(e) => setHuaweiForm(prev => ({ ...prev, kiosk_key: e.target.value.trim() }))}
                            disabled={!!editingHuawei}
                            data-testid="huawei-key-input"
                          />
                          <Button variant="outline" onClick={handleTestHuaweiKey} disabled={testingHuawei} data-testid="huawei-test-btn">
                            {testingHuawei ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Testar'}
                          </Button>
                        </div>
                        {huaweiTestResult && huaweiTestResult.success && (
                          <div className="text-xs bg-green-50 border border-green-200 rounded p-2 text-green-800">
                            <CheckCircle className="h-3 w-3 inline mr-1" />
                            {huaweiTestResult.station_overview?.stationName} — {huaweiTestResult.station_overview?.plantAddress} —
                            {' '}Potência atual: {huaweiTestResult.real_kpi?.realTimePower} kW
                          </div>
                        )}
                        {huaweiTestResult && !huaweiTestResult.success && (
                          <div className="text-xs bg-red-50 border border-red-200 rounded p-2 text-red-700">
                            <XCircle className="h-3 w-3 inline mr-1" />
                            {huaweiTestResult.error}
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Local *</Label>
                          <Input
                            placeholder="Ex: Pragal"
                            value={huaweiForm.local}
                            onChange={(e) => setHuaweiForm(prev => ({ ...prev, local: e.target.value }))}
                            data-testid="huawei-local-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Potência Instalada (kW)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={huaweiForm.installed_power_kw}
                            onChange={(e) => setHuaweiForm(prev => ({ ...prev, installed_power_kw: parseFloat(e.target.value) || 0 }))}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Descrição</Label>
                        <Input
                          placeholder="Ex: Inversor Huawei - Almada"
                          value={huaweiForm.descricao}
                          onChange={(e) => setHuaweiForm(prev => ({ ...prev, descricao: e.target.value }))}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => { setShowHuaweiDialog(false); resetHuaweiForm(); }}>Cancelar</Button>
                      <Button onClick={handleSaveHuawei} data-testid="huawei-save-btn">
                        <Save className="h-4 w-4 mr-2" />
                        {editingHuawei ? 'Atualizar' : 'Adicionar'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {huaweiStations.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Globe className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>Nenhuma estação Huawei configurada</p>
                  <p className="text-xs mt-2">A Kiosk Key encontra-se no URL público partilhado pela FusionSolar (parâmetro <code>kk=</code>)</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Local</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Kiosk Key</TableHead>
                      <TableHead className="text-right">Potência Inst.</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {huaweiStations.map((s) => (
                      <TableRow key={s.id} data-testid={`huawei-row-${s.id}`}>
                        <TableCell className="font-medium">{s.local}</TableCell>
                        <TableCell className="text-gray-600">{s.descricao || '-'}</TableCell>
                        <TableCell className="font-mono text-xs text-gray-500">
                          {s.kiosk_key.slice(0, 8)}...{s.kiosk_key.slice(-4)}
                        </TableCell>
                        <TableCell className="text-right">{s.installed_power_kw} kW</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => handleEditHuawei(s)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteHuawei(s.id)} data-testid={`huawei-delete-${s.id}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ConfiguracaoFotovoltaico;
